/**
 * Wiederverwendbares Dialog-Template
 * Stellt ein einheitliches Layout für Popups bereit:
 * - Überschrift mit X-Button oben rechts
 * - Content-Bereich
 * - Optionale Action-Buttons unten
 *
 * Exportiert zusätzlich:
 * - DialogSection: Abschnitts-Überschrift mit Gradient-Divider
 * - dialogFieldSx: Einheitliches Styling für Formularfelder in Dialogen
 */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { colors } from "../../styles/colors";
import { borderRadius, fonts } from "../../styles/theme";

interface DialogTemplateProps {
  /** Steuert ob der Dialog geöffnet ist */
  open: boolean;
  /** Callback zum Schließen (X-Button und Backdrop-Klick) */
  onClose: () => void;
  /** Überschrift des Dialogs */
  title: string;
  /** Kleine graue Zeile unter der Überschrift, z. B. der volle Wortlaut zu einem Kurznamen */
  subtitle?: string;
  /** Inhalt links in der Kopfzeile, z. B. ein Zurück-Link innerhalb eines mehrstufigen Dialogs. */
  headerLeft?: React.ReactNode;
  /** Inhalt des Dialogs */
  children: React.ReactNode;
  /** Optionale Action-Buttons (z.B. Abbrechen/Speichern) */
  actions?: React.ReactNode;
  /** Maximale Breite des Dialogs (Standard: 'sm') */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

/**
 * Einheitliches Styling für einzeilige Formularfelder in Dialogen.
 * Pill-Shape (borderRadius.field), leichter Schatten, konsistente Hover/Focus-Effekte
 * und Floating-Label mit weißem Hintergrund.
 */
export const dialogFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${borderRadius.field}px`,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
    transition: "box-shadow 0.2s ease-in-out",
    "&:hover": { boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)" },
    "&:hover fieldset": { borderColor: colors.border.focus },
    "&.Mui-focused fieldset": {
      borderColor: colors.border.focus,
      borderWidth: 2,
    },
    "&.Mui-focused": { boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)" },
    "& fieldset": { borderRadius: `${borderRadius.field}px` },
  },
  "& .MuiInputLabel-root": {
    backgroundColor: "white",
    padding: "0 4px",
    "&.MuiInputLabel-shrink": { backgroundColor: "white" },
  },
};

interface DialogSectionProps {
  /** Überschrift des Abschnitts */
  title: string;
  /** Erster Abschnitt (kein Divider oben) */
  first?: boolean;
  children: React.ReactNode;
}

/**
 * Abschnitts-Container für Dialog-Formulare.
 * Rendert einen Gradient-Divider (außer beim ersten Abschnitt),
 * eine Uppercase-Überschrift im Sidebar-Kategorie-Stil und den Inhalt.
 */
export const DialogSection: React.FC<DialogSectionProps> = ({
  title,
  first,
  children,
}) => (
  <>
    {!first && (
      <Box
        sx={{
          my: 3,
          height: "1px",
          background: `linear-gradient(to right, transparent, ${colors.border.divider}, transparent)`,
        }}
      />
    )}
    <Typography
      sx={{
        mb: 2,
        fontSize: "0.65rem",
        fontWeight: fonts.weights.semiBold,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: colors.text.secondary,
      }}
    >
      {title}
    </Typography>
    {children}
  </>
);

const DialogTemplate: React.FC<DialogTemplateProps> = ({
  open,
  onClose,
  title,
  subtitle,
  headerLeft,
  children,
  actions,
  maxWidth = "sm",
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth
    slotProps={{ paper: { sx: { borderRadius: 2 } } }}
  >
    <DialogTitle
      sx={{
        display: "grid",
        // Die Überschrift steht in jedem Popup mittig. Bei 1fr auto 1fr sitzt die mittlere
        // Spalte unabhängig von der Breite der beiden Seiten genau in der Mitte des Dialogs –
        // anders als bei space-between, wo der X-Button sie nach links drückt.
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        pb: 2,
      }}
    >
      <Box sx={{ justifySelf: "start" }}>{headerLeft}</Box>
      <Box sx={{ minWidth: 0, textAlign: "center" }}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, mt: 0.5 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ color: "text.secondary", justifySelf: "end" }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <Box
      sx={{
        mx: 3,
        height: "1px",
        background: `linear-gradient(to right, transparent, ${colors.border.divider}, transparent)`,
      }}
    />
    {/*
      Keine eigene Feldhöhe mehr: die kommt zentral aus `formField` (styles/theme.ts) und ist
      dort so berechnet, dass Wert UND Ruhe-Label mittig sitzen. Eine feste Höhe hier hat
      genau das gebrochen – sie verkürzte nur das Feld, ließ das absolut positionierte
      Ruhe-Label aber auf seiner Position und damit zu weit unten stehen.
    */}
    <DialogContent sx={{ pt: 3 }}>{children}</DialogContent>
    {actions && (
      <DialogActions
        sx={{ px: 2, pb: 2, "& .MuiButton-root": { minWidth: 110 } }}
      >
        {actions}
      </DialogActions>
    )}
  </Dialog>
);

export default DialogTemplate;
